import { MapPin, User, Phone, Mail, Star } from "lucide-react";
import { COMPANY } from "../lib/constants";

const branches = [
  {
    id: "ny",
    name: "Sucursal New York",
    locationLabel: "Dirección",
    location: "405 Broadway, Port Jefferson Station, NY 11776",
    contactName: "Manuel Agramonte",
    phones: ["(631) 476-0779"],
    badge: "NY",
  },
  {
    id: "oh",
    name: "Sucursal Ohio",
    locationLabel: "Cobertura",
    location: "Cincinnati, Ohio y Columbus, Ohio",
    contactName: "Victor Luna",
    phones: ["(513) 571-1173", "(513) 257-1279"],
    badge: "OH",
  },
];

export default function Branches() {
  return (
    <section
      id="sucursales"
      data-testid="branches-section"
      className="py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-sm uppercase tracking-[0.25em] font-bold text-[#D61F26] mb-3">
            Donde estamos
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-[#0B1F4D]">
            Nuestras Sucursales
          </h2>
          <div className="mt-3 mx-auto w-24 h-1 bg-[#D61F26]" />
          <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">
            Atención cercana en cada ciudad — visítanos o llámanos a la sucursal
            más cercana a ti.
          </p>
        </div>

        {/* Headquarters — featured card */}
        <div
          data-testid="branch-card-matriz"
          className="mt-12 relative bg-[#0B1F4D] text-white shadow-2xl overflow-hidden"
        >
          {/* Top ribbon */}
          <div className="absolute top-0 left-0 right-0 h-1.5 flex">
            <div className="flex-1 bg-[#D61F26]" />
            <div className="flex-1 bg-white" />
            <div className="flex-1 bg-[#002D62]" />
          </div>

          {/* Floating "MATRIZ" badge */}
          <div className="absolute top-5 right-5 sm:top-7 sm:right-7 flex items-center gap-1.5 bg-[#D61F26] text-white px-3 py-1.5 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-extrabold shadow-lg">
            <Star className="w-3.5 h-3.5 fill-white" strokeWidth={2.5} />
            Oficina Principal
          </div>

          <div className="p-7 sm:p-10 lg:p-12">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#D61F26] mb-2">
              Sede Central · Matriz
            </div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white leading-tight">
              Oficina Principal
            </h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-full bg-[#D61F26] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <div className="pt-1">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-white/60">
                    Dirección
                  </div>
                  <div className="mt-1 font-display font-bold text-white leading-snug">
                    415 E 176 St
                    <br />
                    Bronx, NY 10457
                  </div>
                </div>
              </div>

              {/* Phones */}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-full bg-[#25D366] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <div className="pt-1">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-white/60">
                    Teléfonos
                  </div>
                  <div className="mt-1 flex flex-col font-display font-bold text-white">
                    <a
                      data-testid="matriz-phone-0"
                      href="tel:+17185765760"
                      className="hover:text-[#FBD300] transition-colors"
                    >
                      (718) 576-5760
                    </a>
                    <a
                      data-testid="matriz-phone-1"
                      href="tel:+17186001183"
                      className="hover:text-[#FBD300] transition-colors"
                    >
                      (718) 600-1183
                    </a>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <div className="pt-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-white/60">
                    Correo
                  </div>
                  <a
                    data-testid="matriz-email"
                    href={`mailto:${COMPANY.email}`}
                    className="mt-1 inline-block font-display font-bold text-white hover:text-[#FBD300] transition-colors break-all"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary branches */}
        <div className="mt-10 sm:mt-12 mb-4 text-center">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-gray-500">
            Sucursales Asociadas
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {branches.map((b) => (
            <div
              key={b.id}
              data-testid={`branch-card-${b.id}`}
              className="relative bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 hover:border-[#0B1F4D]"
            >
              <div className="h-1 bg-[#D61F26]" />

              <div className="p-7 lg:p-9">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#D61F26]">
                      Sucursal
                    </div>
                    <h3 className="mt-1 font-display text-2xl lg:text-3xl font-extrabold uppercase tracking-tight text-[#0B1F4D] leading-tight">
                      {b.name.replace("Sucursal ", "")}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#0B1F4D] flex items-center justify-center font-display font-black text-white text-base shadow-md">
                    {b.badge}
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#D61F26]/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#D61F26]" strokeWidth={2.25} />
                    </div>
                    <div className="pt-1">
                      <div className="text-xs uppercase tracking-wider font-bold text-gray-500">
                        {b.locationLabel}
                      </div>
                      <div className="mt-0.5 font-semibold text-[#0B1F4D]">
                        {b.location}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#0B1F4D]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#0B1F4D]" strokeWidth={2.25} />
                    </div>
                    <div className="pt-1">
                      <div className="text-xs uppercase tracking-wider font-bold text-gray-500">
                        Contacto
                      </div>
                      <div className="mt-0.5 font-semibold text-[#0B1F4D]">
                        {b.contactName}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#25D366]" strokeWidth={2.25} />
                    </div>
                    <div className="pt-1">
                      <div className="text-xs uppercase tracking-wider font-bold text-gray-500">
                        {b.phones.length > 1 ? "Teléfonos" : "Teléfono"}
                      </div>
                      <div className="mt-0.5 flex flex-col">
                        {b.phones.map((p, i) => (
                          <a
                            key={i}
                            data-testid={`branch-${b.id}-phone-${i}`}
                            href={`tel:+1${p.replace(/\D/g, "")}`}
                            className="font-semibold text-[#0B1F4D] hover:text-[#D61F26] transition-colors"
                          >
                            {p}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
